import type { Book } from '@/utils/bookRouting';
import type { ReaderBook } from './booksApi';

/**
 * Disk backing for the catalog and for reader payloads of books the user has
 * opened, so a cold start with no network renders a library instead of an
 * empty shelf.
 *
 * localStorage rather than cookies: on iOS the app runs from
 * capacitor://localhost, where cookie writes are dropped.
 *
 * Everything here is best-effort. Storage can be absent, full, or hold a
 * half-written value from a previous version, and none of those are worth
 * failing a page load over - the network copy is always the source of truth.
 */

// Bumped when the persisted shape changes. Entries written by an older version
// are ignored and cleaned up rather than parsed into something the reader would
// choke on.
const SCHEMA_VERSION = 1;

const CATALOG_KEY = `aleph:catalog:v${SCHEMA_VERSION}`;
const READER_BOOK_PREFIX = `aleph:reader-book:v${SCHEMA_VERSION}:`;

/**
 * A reader payload is roughly 1 KB per chapter, so a long book lands near
 * 100 KB. Twelve of them sit well inside the ~5 MB localStorage budget while
 * covering far more books than anyone reads at once.
 */
const MAX_READER_BOOKS = 12;

/**
 * A single payload past this is refused outright. Evicting the other eleven
 * entries to make room for one pathological book is a worse outcome than not
 * caching that book at all.
 */
const MAX_ENTRY_BYTES = 1_500_000;

interface Envelope<T> {
  savedAt: number;
  payload: T;
}

const readEnvelope = <T>(key: string, isValid: (payload: unknown) => payload is T): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const envelope = JSON.parse(raw) as Envelope<unknown>;
    if (!envelope || typeof envelope.savedAt !== 'number' || !isValid(envelope.payload)) {
      localStorage.removeItem(key);
      return null;
    }

    return envelope.payload;
  } catch {
    // Unreadable or unparseable: drop it so it stops costing a parse on every
    // boot, and fall back to the network.
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage is unavailable entirely; nothing to clean up.
    }
    return null;
  }
};

const readerBookKeys = (): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(READER_BOOK_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
};

/**
 * Drop everything past the MAX_READER_BOOKS most recently written entries.
 *
 * Ranked by `savedAt`, with an unreadable entry ranking 0 so corrupt values are
 * the first to go. Ties break on the key so two entries sharing a timestamp
 * evict in the same order everywhere, rather than by store iteration order.
 */
const pruneReaderBooks = (keep = MAX_READER_BOOKS): void => {
  const entries = readerBookKeys().map((key) => {
    let savedAt = 0;
    try {
      savedAt = (JSON.parse(localStorage.getItem(key) ?? '') as Envelope<unknown>)?.savedAt ?? 0;
    } catch {
      savedAt = 0;
    }
    return { key, savedAt };
  });

  if (entries.length <= keep) {
    return;
  }

  entries.sort((a, b) => (b.savedAt - a.savedAt) || a.key.localeCompare(b.key));
  for (const { key } of entries.slice(keep)) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing to do; the next write will try again.
    }
  }
};

/**
 * Write, and treat a full store as recoverable: prune hard, try once more, then
 * give up. A failed cache write must never surface to the caller.
 */
const writeEnvelope = (key: string, payload: unknown): void => {
  let serialized: string;
  try {
    serialized = JSON.stringify({ savedAt: Date.now(), payload } satisfies Envelope<unknown>);
  } catch {
    return;
  }

  if (serialized.length > MAX_ENTRY_BYTES) {
    return;
  }

  try {
    localStorage.setItem(key, serialized);
    return;
  } catch {
    // Most likely the quota. Fall through to a prune-and-retry.
  }

  try {
    pruneReaderBooks(Math.floor(MAX_READER_BOOKS / 2));
    localStorage.setItem(key, serialized);
  } catch {
    // Still no room. The network copy remains authoritative, so this is fine.
  }
};

const isBookArray = (payload: unknown): payload is Book[] =>
  Array.isArray(payload) && payload.every((book) => typeof (book as Book)?.id === 'string');

const isReaderBook = (payload: unknown): payload is ReaderBook => {
  const book = payload as ReaderBook | null;
  return !!book && typeof book.id === 'string' && Array.isArray(book.chapters);
};

export const readCachedCatalog = (): Book[] | null => {
  const books = readEnvelope(CATALOG_KEY, isBookArray);
  return books && books.length > 0 ? books : null;
};

export const writeCachedCatalog = (books: Book[]): void => {
  if (books.length === 0) {
    // An empty catalog is almost always a failed request rather than an empty
    // library; keeping the previous list is the better guess.
    return;
  }
  writeEnvelope(CATALOG_KEY, books);
};

export const readCachedReaderBook = (bookId: string): ReaderBook | null =>
  readEnvelope(`${READER_BOOK_PREFIX}${bookId}`, isReaderBook);

export const writeCachedReaderBook = (bookId: string, book: ReaderBook): void => {
  writeEnvelope(`${READER_BOOK_PREFIX}${bookId}`, book);
  pruneReaderBooks();
};

export const removeCachedReaderBook = (bookId: string): void => {
  try {
    localStorage.removeItem(`${READER_BOOK_PREFIX}${bookId}`);
  } catch {
    // Storage unavailable; nothing persisted to remove.
  }
};

/** Drops every persisted reader payload and the catalog. */
export const clearReaderCache = (): void => {
  try {
    for (const key of readerBookKeys()) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem(CATALOG_KEY);
  } catch {
    // Storage unavailable; nothing persisted to clear.
  }
};
